import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { richTextListStyles } from "../../common/richTextListStyles"
import { typographyVariants } from "../../theme/typography"
import { breakpoints, layoutPadding } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

export const OuterRoot = styled.div`
  width: 100%;
  overflow-x: clip;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[40]}px;
  padding: ${sizes.spacing[48]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
    padding: ${sizes.spacing[64]}px 0;
  }
`

export const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
  }

  .MuiTabs-root {
    width: 100%;
    max-width: 100%;
  }

  .MuiTabs-flexContainer {
    border-bottom: 1px solid ${colors.primitives.neutral200};
  }

  /* EGDE-508: no font-weight override — the theme keeps unselected tabs
     regular and only .Mui-selected bold, exactly like the design. */
  .MuiTab-root {
    @media (max-width: ${breakpoints.lg - 1}px) {
      font-size: 18px;
      /* EGDE-508 r2: MUI's 90px min-width centred short labels in wide
         boxes — the mobile design has compact labels ~32px apart. */
      min-width: 0;
    }
  }

  @media (max-width: ${breakpoints.lg - 1}px) {
    .MuiTabs-flexContainer {
      gap: ${sizes.spacing[32]}px;
    }
  }
`

export const TitleRow = styled.div`
  display: flex;
  /* EGDE-476: the title rich text can span several lines — pin both children
     to the top so the decorative chevron sits beside the FIRST line instead
     of floating vertically centred against the whole block. */
  align-items: flex-start;
  gap: ${sizes.spacing[6]}px;
  width: 100%;
`

export const Title = styled(Typography)`
  color: ${colors.light.text.primary};

  /* EGDE-464/476: the title is CMS rich text rendered inside an h2-typography
     wrapper. Every block element the editor can insert (paragraphs, any
     heading level) renders at the title's own typography — a raw h1 painted
     2em glyphs over the wrapper's fixed h2 line-height, overlapping the
     lines. Blocks keep their natural block display: inline display fused
     adjacent paragraphs into one unspaced run of text. */
  & p,
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  /* EGDE-476: hyperlinks follow the site-wide rich-text link style
     (EGDE-327/465); !important overrides inline colors pasted into the RTE. */
  & a {
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-476: RTE media can't be allowed to explode a section title. */
  & img {
    max-width: 100%;
    height: auto;
  }

  /* EGDE-476: lists dropped into the title get the shared brand-chevron
     bullets instead of default black discs — with the marker metrics scaled
     to the title's font size rather than the shared body-copy px values. */
  ${richTextListStyles}

  & ul,
  & ol {
    margin-block: 0.25em;
  }

  & ul > li {
    padding-inline-start: 0.75em;
  }

  & ul > li::before {
    width: 0.3em;
    height: 0.5em;
    top: 0.3em;
  }
` as typeof Typography

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  /* EGDE-476: box the chevron to exactly one title line so it stays centred
     on the first line while TitleRow aligns flex-start (multi-line titles). */
  height: ${typographyVariants.h2.mobile.lineHeight}px;

  @media (min-width: ${breakpoints.lg}px) {
    height: ${typographyVariants.h2.desktop.lineHeight}px;
  }

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const GallerySwiperContainer = styled.div`
  width: 100%;
  overflow: visible;

  .swiper {
    width: 100%;
    overflow: visible;
    /* EGDE-508: the legacy global .swiper rule adds 50px top/bottom padding,
       doubling the section's own 40/48px gaps around the image row. */
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-inline-start: ${layoutPadding.mobile}px !important;
    padding-inline-end: 0 !important;

    @media (min-width: ${breakpoints.lg}px) {
      padding-inline-start: max(
        ${layoutPadding.desktop}px,
        calc((100% - ${breakpoints.lg}px) / 2 + ${layoutPadding.desktop}px)
      ) !important;
    }

    @media (min-width: ${breakpoints.xl}px) {
      padding-inline-start: max(
        ${layoutPadding.desktopL}px,
        calc((100% - ${breakpoints.xl}px) / 2 + ${layoutPadding.desktopL}px)
      ) !important;
    }

    @media (min-width: ${breakpoints.xxl}px) {
      padding-inline-start: max(
        ${layoutPadding.desktopXl}px,
        calc((100% - ${breakpoints.xxl}px) / 2 + ${layoutPadding.desktopXl}px)
      ) !important;
    }
  }

  .swiper-slide {
    width: auto;
  }
`

export const GalleryItem = styled.div`
  position: relative;
  width: 300px;
  height: 191px;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;

  /* EGDE-574: the design zooms the media on hover ("enlarges and shrinks")
     instead of the flat opacity dip that used to stand in for it. The media
     scales INSIDE the card — the card keeps its size, so neighbouring slides
     never shift — and the existing overflow:hidden clips it to the radius.
     Iframes are left out: a YouTube embed swallows pointer events, so it would
     never see the hover anyway. */
  img,
  video {
    transition: transform 0.4s cubic-bezier(0.33, 1, 0.68, 1);
  }

  &:hover img,
  &:hover video {
    transform: scale(1.06);
  }

  @media (prefers-reduced-motion: reduce) {
    img,
    video {
      transition: none;
    }

    &:hover img,
    &:hover video {
      transform: none;
    }
  }

  @media (min-width: ${breakpoints.lg}px) {
    width: 384px;
    height: 245px;
  }
`

/* ==================================================================== */
/* EGDE-574: video play/pause control                                    */
/* A video slide was indistinguishable from a photo — QA filed it as a    */
/* regression ("no visual indicator that a video is a video") and asked   */
/* for a play AND pause button on the component. Figma's video card       */
/* variant carries a 32px badge inset 16px from the top inline-END corner */
/* (visually left in the RTL design): a black disc at 50% over the media  */
/* with a white glyph.                                                   */
/* It is a real button — it plays/pauses the slide's video in place, and  */
/* swallows the click so the card's lightbox doesn't also open.           */
/* ==================================================================== */
export const VideoToggle = styled.button`
  position: absolute;
  top: 16px;
  inset-inline-end: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: ${colors.primitives.neutral0};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.72);
  }
`

/* Breakpoint media slots. EGDE-633: despite the names, each one holds whatever
   medium the editor attached to its field — an <Image> or a <video> plus its
   play/pause badge. Both are inset:0 over the card, so an absolutely positioned
   VideoToggle inside lands in the same corner it would as a child of the card. */
export const GalleryImage = styled.div<{ $hiddenOnMobile?: boolean }>`
  position: absolute;
  inset: 0;

  /* EGDE-468: when the editor supplied a mobile crop, the desktop image only
     renders from the desktop breakpoint up. */
  ${(p) =>
    p.$hiddenOnMobile
      ? `
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
  `
      : ""}
`

/* EGDE-468: mobile-only counterpart of GalleryImage (see the note above). */
export const GalleryImageMobile = styled.div`
  position: absolute;
  inset: 0;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const GalleryVideo = styled.div`
  position: absolute;
  inset: 0;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`

export const GalleryNativeVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/* EGDE-424: per-category description is CMS rich text. Body-copy typography;
   paragraph margins collapse to simple spacing. */
export const RichDescription = styled.div`
  color: ${colors.light.text.primary};
  width: 100%;
  text-align: start;
  /* EGDE-651/EGDE-652: the running text is the body2 pair — 16px on mobile
     (already the design value, left alone) and 18px on desktop, where it was
     still rendering at the mobile 16px. 26px is body2's desktop leading. */
  font-size: 16px;
  line-height: 24px;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
    line-height: 26px;
  }

  & p {
    margin: 0 0 ${sizes.spacing[8]}px;
  }

  & p:last-child {
    margin-bottom: 0;
  }

  & a {
    /* EGDE-465 (same rule as EGDE-327): rich-text body hyperlinks render blue.
       !important overrides inline color:#... pasted into CMS rich text. */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-330: brand-chevron bullets, shared with every rich-text renderer. */
  ${richTextListStyles}
`

export const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${sizes.spacing[16]}px ${sizes.spacing[24]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${sizes.spacing[16]}px ${sizes.spacing[56]}px;
  }
`

export const FeatureTag = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[8]}px;
  }
`

export const FeatureIcon = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const FeatureLabel = styled(Typography)`
  color: ${colors.light.text.primary};
`

export const CtaSection = styled.div`
  display: flex;
  /* EGDE-467: long CTA labels pushed the button past the 375px viewport edge.
     The row may wrap and the button may shrink instead of overflowing. */
  flex-wrap: wrap;
  align-items: center;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[40]}px;
  }
`

const ctaStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  background: ${colors.gradients.gradientCta};
  border-radius: ${sizes.borderRadius.full}px;
  color: ${colors.light.text.primary};
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  /* EGDE-467: wrap to multiple lines on narrow screens rather than being cut
     out of the viewport. */
  white-space: normal;
  text-align: center;
  max-width: 100%;
  transition: box-shadow 0.2s ease;

  /* EGDE-500: Main CTA sheet — primary hovers to the flat orange with the
     button glow and presses to the darker orange (instant swap, EGDE-355). */
  &:hover,
  &:focus-visible {
    background: ${colors.primitives.primary500};
    box-shadow: ${colors.shadows.button};
  }

  &:active {
    background: ${colors.primitives.primary600};
    box-shadow: none;
  }
`

export const CtaButton = styled(Link)`
  ${ctaStyles}
`

export const CtaButtonExternal = styled.a`
  ${ctaStyles}
`

export const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[2]}px;
`

export const PriceLabel = styled(Typography)`
  color: ${colors.light.text.primary};
`

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${sizes.spacing[4]}px;
`

export const PricePrefix = styled(Typography)`
  color: ${colors.light.text.primary};
`

export const PriceAmount = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
`

/* ------------------------------------------------------------------ */
/* Lightbox                                                            */
/* ------------------------------------------------------------------ */
export const LightboxContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.spacing[16]}px;
`

export const LightboxCloseButton = styled.button`
  position: absolute;
  top: -${sizes.spacing[40]}px;
  /* @noflip */
  right: 0;
  z-index: 1;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`

export const LightboxImageWrapper = styled.div`
  position: relative;
  width: 85vw;
  height: 60vh;

  @media (min-width: ${breakpoints.lg}px) {
    width: 80vw;
    height: 75vh;
  }
`

/* EGDE-466: physical order is enforced with a dir="ltr" HTML attribute on the
   element (set in the JSX) — NOT with a CSS direction rule. A CSS direction
   here gets mirrored by stylis-plugin-rtl in the production build (the @noflip
   escape comment is stripped by minification, see EGDE-262), which re-reversed
   the arrows on the deployed site while looking correct locally. */
export const LightboxNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[16]}px;
`

export const LightboxArrowButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    stroke: #fff;
  }
`

export const LightboxCounter = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
`
